/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Checkbox, Grid, IconButton, makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import Loading from 'components/Loading';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';
import {
  activateMultipleApplicationsRequest,
  editApplicationRequest,
  getApplicationsClientsRequest,
  inactivateMultipleApplicationsRequest,
  newApplicationRequest,
  removeClientApplicationRequest,
  removeMultipleApplicationsRequest,
  setApplicationRequest,
} from 'modules/clients/actions';
import {
  ClientApplicationsFields,
  ClientFields,
  ListClientDataApplicationProps,
} from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import Button from 'components/Button';
import { PermissionsData } from 'modules/authenticate/interfaces';
import ButtonGroup from 'components/ButtonGroup';
import { useParams } from 'react-router';
import { useTableFilters } from 'hooks/useTableFilters';
import FormAddApplication from './FormAddApplication';
import FormIncludeApplication from './FormIncludeApplication';
import FormSimulateApp from './FormSimulateApp';
import ScreenStatesApplications from './ScreenStatesApplications';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'edit_application',
  'delete_application',
];

interface Props {
  listApplications: ListClientDataApplicationProps;
  isLoading: boolean;
  client: ClientFields | null;
  permissions: PermissionsData | undefined;
  getApplicationsClientsRequest: ({ id, query, type }) => void;
  removeClientApplicationRequest: (props: {
    clientUuid: string;
    appUuid?: string;
    appCodes?: string[];
  }) => void;
  newApplicationRequest: (props: {
    id: string;
    type: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  editApplicationRequest: (props: {
    id: string;
    type: string;
    app_uuid: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  setApplicationRequest: (props: {
    id: string;
    type: string;
    app_uuid: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  activateMultipleApplicationsRequest: ({ id, app_uuids }) => void;
  inactivateMultipleApplicationsRequest: ({ id, app_uuids }) => void;
  removeMultipleApplicationsRequest: ({ id, app_uuids }) => void;
}

const DefaultApps = ({
  listApplications,
  isLoading,
  client,
  permissions,
  getApplicationsClientsRequest,
  removeClientApplicationRequest,
  newApplicationRequest,
  editApplicationRequest,
  setApplicationRequest,
  modalClose,
  modalOpen,
  activateMultipleApplicationsRequest,
  inactivateMultipleApplicationsRequest,
  removeMultipleApplicationsRequest,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
  } = useTableFilters(initialQuery);
  const [isPlatform, setIsPlatform] = React.useState(
    client?.environment.client_apps.registration_source === 'platform'
  );
  const [refresh, setRefresh] = React.useState(false);
  const [selectedAll, setSelectedAll] = React.useState(false);
  const [checks, setChecks] = React.useState<string[]>([]);
  const { id } = useParams<{ id: string }>();

  const handleFormApplication = () => {
    if (!client) return;

    const {
      client_uuid,
      environment: { client_apps },
    } = client;

    modalOpen({
      title: i18n.t(
        isPlatform
          ? 'INCLUDE_APPLICATION_KEY'
          : 'APPLICATIONS_PAGE.ADD_APPLICATION_KEY'
      ),
      maxWidth: 'sm',
      content: isPlatform ? (
        <FormIncludeApplication
          onCancel={modalClose}
          onSubmit={values => {
            newApplicationRequest({
              id: client_uuid,
              type: client_apps.registration_source,
              values: {
                app_codes: values,
              },
              query,
            });
            modalClose();
          }}
        />
      ) : (
        <FormAddApplication
          mode={client_apps.registration_mode}
          onCancel={modalClose}
          onSubmit={values => {
            newApplicationRequest({
              id: client_uuid,
              type: client_apps.registration_source,
              values,
              query,
            });
            modalClose();
          }}
        />
      ),
    });
  };

  const getClientApplications = (refresh = false) => {
    if (client) {
      getApplicationsClientsRequest({
        id: client?.client_uuid,
        type: client?.environment.client_apps.registration_source,
        query,
      });

      setIsPlatform(
        client?.environment.client_apps.registration_source === 'platform'
      );

      setRefresh(refresh);
    }
  };

  React.useEffect(() => {
    getClientApplications();
  }, [id, query]);

  const handleOpenTestApplication = () => {
    if (!client) return;

    modalOpen({
      title: i18n.t('TEST_OPEN_KEY'),
      content: <FormSimulateApp client={client} />,
    });
  };

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      listApplications.results.forEach(item =>
        ids.push(isPlatform ? item?.app_code : item?.app_uuid)
      );
      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleCheck = (uuid: string) => {
    const findCheckId = checks.find(item => item === uuid);
    if (!findCheckId) {
      setChecks([...checks, uuid]);
    } else {
      const index = checks.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const getButtonGroupOptions = () =>
    [
      {
        label: i18n.t('TEST_OPEN_KEY'),
        hidden: !!permissions && !permissions.can_simulate_applications,
        onClick: handleOpenTestApplication,
      },
      {
        label: i18n.t(
          isPlatform ? 'INCLUDE_APPLICATION_KEY' : 'APPLICATION_KEY'
        ),
        hidden:
          !!permissions &&
          (isPlatform
            ? !permissions?.include_application
            : !permissions?.create_new_application),
        onClick: handleFormApplication,
      },
      {
        label: i18n.t('BUTTON_ACTIVATE_APPLICATIONS_KEY'),
        disabled: checks.length === 0,
        hidden: isPlatform || (!!permissions && !permissions.edit_application),
        onClick: () =>
          modalOpen({
            title: i18n.t('WANT_ACTIVATE_APPLICATIONS_KEY'),
            content: i18n.t('ACTIVATE_APPLICATIONS_SELECTED_DESCRIPTION_KEY'),
            footer: (
              <Grid>
                <Button
                  submitButton
                  id="activate-applications-button"
                  onClick={() => {
                    activateMultipleApplicationsRequest({
                      id,
                      app_uuids: checks,
                    });
                    setTimeout(() => {
                      setChecks([]);
                    }, 1300);
                    modalClose();
                  }}
                >
                  {i18n.t('ACTIVATE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            ),
          }),
      },
      {
        label: i18n.t('BUTTON_INACTIVATE_APPLICATIONS_KEY'),
        disabled: checks.length === 0,
        hidden: isPlatform || (!!permissions && !permissions.edit_application),
        onClick: () =>
          modalOpen({
            title: i18n.t('WANT_INACTIVATE_APPLICATIONS_KEY'),
            content: i18n.t('INACTIVATE_APPLICATIONS_SELECTED_DESCRIPTION_KEY'),
            footer: (
              <Grid>
                <Button
                  submitButton
                  id="inactivate-applications-button"
                  onClick={() => {
                    inactivateMultipleApplicationsRequest({
                      id,
                      app_uuids: checks,
                    });
                    setTimeout(() => {
                      setChecks([]);
                    }, 1300);
                    modalClose();
                  }}
                >
                  {i18n.t('DISABLE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            ),
          }),
      },
      {
        label: i18n.t('BUTTON_REMOVE_APPLICATIONS_KEY'),
        disabled: checks.length === 0,
        hidden:
          !!permissions &&
          (!permissions.delete_application ||
            !permissions.remove_included_application),
        onClick: () =>
          modalOpen({
            title: i18n.t('WANT_REMOVE_APPLICATIONS_KEY'),
            content: i18n.t('REMOVE_APPLICATIONS_SELECTED_DESCRIPTION_KEY'),
            footer: (
              <Grid>
                <Button
                  submitButton
                  id="remove-applications-button"
                  onClick={() => {
                    if (isPlatform) {
                      removeClientApplicationRequest({
                        clientUuid: id,
                        appCodes: checks,
                      });
                    } else {
                      removeMultipleApplicationsRequest({
                        id,
                        app_uuids: checks,
                      });
                    }
                    setTimeout(() => {
                      setChecks([]);
                    }, 1300);
                    modalClose();
                  }}
                >
                  {i18n.t('CONFIRM_KEY')}
                </Button>

                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            ),
          }),
      },
    ].filter(option => !option?.hidden);

  const toggleApplication = (item: ClientApplicationsFields) => {
    const actionType = item?.state === 'active' ? 'DISABLE' : 'ENABLE';
    modalOpen({
      title: i18n.t(`APPLICATIONS_PAGE.${actionType}_APPLICATIONS_KEY`),
      content: i18n.t(
        `APPLICATIONS_PAGE.${actionType}_APPLICATIONS_MINUTES_KEY`
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              setApplicationRequest({
                id: client?.client_uuid || '',
                type: client?.environment.client_apps.registration_source || '',
                app_uuid: item?.app_uuid,
                values: {
                  state: item?.state === 'active' ? 'inactive' : 'active',
                },
                query,
              });
              modalClose();
            }}
          >
            {i18n.t(`${actionType}_KEY`)}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const getMenuActionItems = (item: ClientApplicationsFields) => {
    if (!client) return [];

    return [
      {
        key: 'btn-edit-application',
        label: i18n.t('EDIT_KEY'),
        onClick: () =>
          modalOpen({
            title: i18n.t('APPLICATIONS_PAGE.EDIT_APLICATIONS_KEY'),
            maxWidth: 'sm',
            content: (
              <FormAddApplication
                initialValues={item}
                onCancel={modalClose}
                onSubmit={({
                  app_uuid,
                  ...values
                }: ClientApplicationsFields) => {
                  editApplicationRequest({
                    id: client?.client_uuid,
                    type: client?.environment.client_apps.registration_source,
                    app_uuid,
                    values,
                    query,
                  });

                  modalClose();
                }}
                mode={client?.environment.client_apps.registration_mode}
              />
            ),
          }),
        hidden: !permissions?.edit_application,
      },
      {
        key: 'btn-disable-application',
        label: i18n.t(item?.state === 'active' ? 'DISABLE_KEY' : 'ENABLE_KEY'),
        onClick: () => toggleApplication(item),
        hidden: !permissions?.edit_application,
      },
      {
        key: 'btn-remove-application',
        label: i18n.t('REMOVE_KEY'),
        onClick: () =>
          modalOpen({
            title: i18n.t('APPLICATIONS_PAGE.REMOVE_APPLICATIONS_KEY'),
            content: i18n.t(
              'APPLICATIONS_PAGE.IRREVERSIBLE_ACTION_APPLICATION_KEY'
            ),
            footer: (
              <Grid>
                <Button
                  submitButton
                  id="submit-button"
                  onClick={() => {
                    removeClientApplicationRequest({
                      clientUuid: client?.client_uuid,
                      appUuid: item?.app_uuid,
                    });
                    modalClose();
                  }}
                >
                  {i18n.t('REMOVE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            ),
          }),
        hidden: !permissions?.delete_application,
      },
    ].filter(action => !action?.hidden);
  };

  return (
    <Grid container>
      {client ? (
        <>
          <Grid container className={classes.content}>
            <Grid
              container
              justify="flex-end"
              alignItems="center"
              className={classes.actions}
            >
              <RefreshButton
                id="btn-refresh"
                onClick={() => getClientApplications(true)}
                animate={isLoading}
                disabled={isLoading}
              />
              {getButtonGroupOptions()?.length > 0 && (
                <ButtonGroup
                  style={{ marginLeft: 16 }}
                  label={i18n.t('GENERAL_ACTIONS_KEY')}
                  options={getButtonGroupOptions()}
                />
              )}
            </Grid>
            <TableItems
              header={
                <>
                  {getButtonGroupOptions()?.length > 0 && (
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        onChange={handleAllCheck}
                        id="select-all-applications-clients"
                      />
                    </TableCell>
                  )}
                  <TableCell
                    handleOrder={() =>
                      handleOrder(isPlatform ? 'name' : 'nome')
                    }
                    order
                    className={classes.rowAllRows}
                  >
                    {i18n.t('NAME_KEY')}
                  </TableCell>
                  <TableCell
                    handleOrder={() =>
                      handleOrder(isPlatform ? 'code' : 'estado')
                    }
                    order
                    centerTitle
                    className={classes.statusHeader}
                  >
                    {i18n.t(isPlatform ? 'CODE_KEY' : 'STATUS_KEY')}
                  </TableCell>

                  <Permission
                    value={
                      isPlatform
                        ? 'remove_included_application'
                        : requiredActionsPermissions
                    }
                  >
                    <TableCell centerTitle>
                      <Grid className={classes.actionHeader}>
                        {i18n.t('ACTIONS_KEY')}
                      </Grid>
                    </TableCell>
                  </Permission>
                </>
              }
              renderItems={(item: ClientApplicationsFields, index) => (
                <TableRow key={item?.app_uuid}>
                  {getButtonGroupOptions()?.length > 0 && (
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        onChange={() =>
                          handleCheck(
                            isPlatform ? item?.app_code : item?.app_uuid
                          )
                        }
                        checked={
                          !!checks.find(check =>
                            isPlatform
                              ? check === item?.app_code
                              : check === item?.app_uuid
                          )
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell className={classes.rowAllRows}>
                    {isPlatform ? item?.app_name : item?.name}
                  </TableCell>
                  <TableCell centerTitle>
                    {isPlatform ? (
                      item?.app_code
                    ) : (
                      <ScreenStatesApplications type={item?.state} />
                    )}
                  </TableCell>
                  <Permission
                    value={
                      isPlatform
                        ? 'remove_included_application'
                        : requiredActionsPermissions
                    }
                  >
                    <TableCell align="center">
                      {!isPlatform ? (
                        <MenuActions id={`btn-options-apps-${index}`}>
                          {getMenuActionItems(item)?.length > 0 ? (
                            getMenuActionItems(item)?.map(action => (
                              <MenuActionItem
                                key={action?.key}
                                id={action?.key}
                                onClick={action?.onClick}
                              >
                                {action?.label}
                              </MenuActionItem>
                            ))
                          ) : (
                            <MenuActionItem>
                              {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                            </MenuActionItem>
                          )}
                        </MenuActions>
                      ) : (
                        <IconButton
                          className={classes.removeAppButton}
                          onClick={() =>
                            modalOpen({
                              title: i18n.t(
                                'APPLICATIONS_PAGE.REMOVE_APPLICATIONS_KEY'
                              ),
                              content: i18n.t(
                                'APPLICATIONS_PAGE.IRREVERSIBLE_ACTION_APPLICATION_KEY'
                              ),
                              footer: (
                                <Grid>
                                  <Button
                                    submitButton
                                    id="submit-button"
                                    onClick={() => {
                                      removeClientApplicationRequest({
                                        clientUuid: client?.client_uuid,
                                        appCodes: [item?.app_code],
                                      });
                                      modalClose();
                                    }}
                                  >
                                    {i18n.t('REMOVE_KEY')}
                                  </Button>
                                  <Button
                                    id="cancel-button"
                                    variant="outlined"
                                    onClick={modalClose}
                                  >
                                    {i18n.t('CANCEL_KEY')}
                                  </Button>
                                </Grid>
                              ),
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </Permission>
                </TableRow>
              )}
              data={listApplications}
              isLoading={!refresh && isLoading}
              onChangePage={handlePageChange}
              onChangeLimit={handleLimitChange}
              notFoundText={i18n.t(
                'APPLICATIONS_PAGE.NO_APPLICATIONS_FOUND_KEY'
              )}
            />
          </Grid>
        </>
      ) : (
        <Loading />
      )}
    </Grid>
  );
};

const mapStateToProps = ({
  clients: { isLoading, listApplications, clientSelected },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  isLoading,
  listApplications,
  companySelected,
  role: data?.role,
  client: clientSelected,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getApplicationsClientsRequest,
  removeClientApplicationRequest,
  newApplicationRequest,
  editApplicationRequest,
  setApplicationRequest,
  modalOpen,
  modalClose,
  activateMultipleApplicationsRequest,
  inactivateMultipleApplicationsRequest,
  removeMultipleApplicationsRequest,
})(DefaultApps);
